import type { ReactNode } from 'react';
import { Badge, ChoiceRadio, ChoiceRadioItem, cn } from '@design-system';

export interface Choice<T extends string> {
  value: T;
  title: string;
  description?: ReactNode;
  badge?: string;
  disabled?: boolean;
}

interface ChoiceListProps<T extends string> {
  label: string;
  choices: Choice<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  className?: string;
}

export function ChoiceList<T extends string>({ label, choices, value, onChange, className }: ChoiceListProps<T>) {
  return (
    <ChoiceRadio
      aria-label={label}
      value={value ?? ''}
      onValueChange={next => onChange(next as T)}
      // Pairs sit two per row; odd counts stack full width so no card sits alone or gets squeezed.
      className={cn('grid gap-3', choices.length % 2 === 0 && 'sm:grid-cols-2', className)}
    >
      {choices.map(choice => (
        <ChoiceRadioItem
          key={choice.value}
          value={choice.value}
          title={choice.title}
          description={choice.description}
          disabled={choice.disabled}
          badge={choice.badge && <Badge className="-my-1">{choice.badge}</Badge>}
          className="h-full rounded-xl bg-white p-4 [&_[data-slot=choice-radio-content]]:gap-2 [&_[data-slot=choice-radio-description]]:text-pretty"
        />
      ))}
    </ChoiceRadio>
  );
}
